import React from 'react'
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
} from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { Link, useLocation } from 'react-router-dom'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { userMenu } from '@constants/menuConstants'
import { useMenuChucNang } from 'hooks/useMenuChucNang'
import { LAYOUT_APP_BAR_HEIGHT_PX } from './layoutConstants'
import { cv } from '@theme/cssVars'

const DRAWER_WIDTH = 224
const COLLAPSED_WIDTH = 52
const SIDEBAR_CHILD_INDENT_STEP = 0.375

interface MenuItem {
  key: string
  startPoint: string
  path: string
  label: string
  icon: any
  children: MenuItem[]
}

const SIDEBAR_NESTED_LIST_SX = {
  pt: 0.375,
  pb: 0.5,
  pl: 0.5,
  pr: 0.375,
  ml: 0.5,
  mr: 0.375,
  mb: 0.375,
  borderRadius: '10px',
  backgroundColor: cv('primary', 'surface'),
  border: `1px solid ${cv('primary', 'surfaceBorder')}`,
  borderLeftWidth: 3,
  borderLeftColor: cv('primary', 'main'),
  boxShadow: '0 1px 4px rgba(15, 23, 42, 0.08)',
} as const

const leafActiveSx = {
  backgroundColor: cv('shell', 'sidebarActiveBg'),
  color: cv('shell', 'sidebarActiveText'),
  fontWeight: 600,
  border: `1px solid ${cv('primary', 'surfaceBorder')}`,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
  '&:hover': {
    backgroundColor: cv('shell', 'sidebarActiveBgHover'),
    borderColor: cv('primary', 'light'),
  },
  '& .MuiListItemIcon-root': {
    color: `${cv('shell', 'sidebarActiveIcon')} !important`,
  },
}

const sectionHeaderSx = {
  backgroundColor: 'transparent',
  color: cv('primary', 'dark'),
  fontWeight: 600,
  boxShadow: 'none',
  border: '1px solid transparent',
  '&:hover': { backgroundColor: 'rgba(15, 23, 42, 0.04)' },
  '& .MuiListItemIcon-root': {
    color: `${cv('primary', 'main')} !important`,
  },
}

const inactiveItemSx = {
  color: cv('shell', 'sidebarItem'),
  fontWeight: 500,
  border: '1px solid transparent',
  '& .MuiListItemIcon-root': {
    color: `${cv('shell', 'sidebarIcon')} !important`,
  },
}

const inactiveParentRootSx = {
  color: cv('neutral', 'text'),
  fontWeight: 600,
  border: '1px solid transparent',
  '& .MuiListItemIcon-root': {
    color: `${cv('shell', 'sidebarIcon')} !important`,
  },
}

const inactiveChildItemSx = {
  color: cv('neutral', 'textMuted'),
  fontWeight: 500,
  border: '1px solid transparent',
  '& .MuiListItemIcon-root': {
    color: `${cv('neutral', 'textSubtle')} !important`,
  },
}

const expandedParentSx = {
  backgroundColor: cv('shell', 'sidebarSectionBg'),
  boxShadow: 'none',
  border: `1px solid ${cv('neutral', 'borderMedium')}`,
}

interface SidebarMenuProps {
  openSidebar?: boolean
  isMobile?: boolean
  mobileOpen?: boolean
  onMobileClose?: () => void
}

const paperSx = {
  backgroundColor: cv('neutral', 'background'),
  borderRight: `1px solid ${cv('shell', 'sidebarBorder')}`,
  overflowX: 'hidden' as const,
  '& a.MuiListItemButton-root': {
    textDecoration: 'none !important',
  },
  '& .MuiListItemButton-root': {
    justifyContent: 'flex-start',
    transition:
      'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(15, 23, 42, 0.045)',
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${cv('primary', 'surfaceBorder')}`,
      outlineOffset: 1,
    },
  },
  '& .MuiListItemIcon-root': {
    minWidth: 32,
    justifyContent: 'center',
    '& svg': { fontSize: '1.125rem' },
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.96875rem',
    letterSpacing: '0.0125em',
    lineHeight: 1.45,
  },
  '& .MuiListItemText-root': {
    minWidth: 0,
  },
}

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 991px)').matches

/** Sidebar — port gần như nguyên bản từ ytl-logistics-frontend. */
const SidebarMenu: React.FC<SidebarMenuProps> = ({
  openSidebar = true,
  isMobile: isMobileProp,
  mobileOpen = false,
  onMobileClose,
}) => {
  const { pathname } = useLocation()
  const { hasMenu } = useMenuChucNang()
  const [isMobileState, setIsMobileState] = React.useState(isMobileViewport)
  const isMobile = isMobileProp ?? isMobileState
  const visibleMenuItems = React.useMemo(() => userMenu(hasMenu) as MenuItem[], [hasMenu])
  const [expandedMenus, setExpandedMenus] = React.useState<Record<string, boolean>>({})
  const [collapsedMenuAnchorEl, setCollapsedMenuAnchorEl] = React.useState<HTMLElement | null>(null)
  const [collapsedMenuParentKey, setCollapsedMenuParentKey] = React.useState<string | null>(null)
  const [collapsedMenuChildren, setCollapsedMenuChildren] = React.useState<MenuItem[]>([])

  React.useEffect(() => {
    if (isMobileProp != null) return undefined
    const onResize = () => setIsMobileState(isMobileViewport())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [isMobileProp])

  const drawerWidth = openSidebar ? DRAWER_WIDTH : isMobile ? DRAWER_WIDTH : COLLAPSED_WIDTH

  const isRouteSelected = React.useCallback(
    (item: MenuItem) => {
      if (item.path && (pathname === item.path || pathname.startsWith(`${item.path}/`))) {
        return true
      }
      // startPoint phải là đoạn path đủ dài (≥3) để tránh match nhầm (vd. "so-du")
      if (item.startPoint && item.startPoint.length >= 3 && pathname.includes(item.startPoint)) {
        return true
      }
      return false
    },
    [pathname],
  )

  const isItemSelected = React.useCallback(
    (item: MenuItem): boolean => {
      if (isRouteSelected(item)) return true
      return item.children?.some((child) => isItemSelected(child)) ?? false
    },
    [isRouteSelected],
  )

  const autoExpandedMenus = React.useMemo(() => {
    const nextExpanded: Record<string, boolean> = {}
    const markExpandedParents = (items: MenuItem[]): boolean => {
      let branchMatched = false
      items.forEach((item) => {
        const selfMatched = isRouteSelected(item)
        const childMatched = item.children ? markExpandedParents(item.children) : false
        if ((selfMatched || childMatched) && item.children?.length) {
          nextExpanded[item.key] = true
        }
        if (selfMatched || childMatched) branchMatched = true
      })
      return branchMatched
    }
    markExpandedParents(visibleMenuItems)
    return nextExpanded
  }, [isRouteSelected, visibleMenuItems])

  const autoOpenOtherRootKey = React.useMemo(() => {
    for (const item of visibleMenuItems) {
      if (!item.children?.length) continue
      if (autoExpandedMenus[item.key]) return item.key
    }
    return null
  }, [autoExpandedMenus, visibleMenuItems])

  React.useEffect(() => {
    setExpandedMenus({})
  }, [autoOpenOtherRootKey])

  const toggleMenu = (key: string, rootKey?: string) => {
    setExpandedMenus((prev) => {
      const currentlyOpen = Object.prototype.hasOwnProperty.call(prev, key)
        ? prev[key]
        : autoExpandedMenus[key] ?? false
      const isRoot = visibleMenuItems.some((item) => item.key === key)

      if (isRoot) {
        // Accordion giữa các nhóm gốc (Chấm công / Đơn từ / …)
        const next: Record<string, boolean> = {}
        for (const item of visibleMenuItems) {
          if (!item.children?.length) continue
          next[item.key] = item.key === key ? !currentlyOpen : false
        }
        return next
      }

      // Menu lồng (vd. Nghỉ phép trong Đơn từ): chỉ toggle mục đó, giữ nhóm gốc mở
      const next: Record<string, boolean> = { ...prev, [key]: !currentlyOpen }
      if (rootKey) next[rootKey] = true
      return next
    })
  }

  const closeCollapsedMenu = React.useCallback(() => {
    setCollapsedMenuAnchorEl(null)
    setCollapsedMenuParentKey(null)
    setCollapsedMenuChildren([])
  }, [])

  const handleParentClick = (
    event: React.MouseEvent<HTMLElement>,
    item: MenuItem,
    hasChildren: boolean,
    rootKey?: string,
  ) => {
    if (!hasChildren) return
    if (openSidebar || isMobile) {
      toggleMenu(item.key, rootKey)
      return
    }
    const nextChildren = item.children ?? []
    const isSameParentOpening =
      collapsedMenuParentKey === item.key && Boolean(collapsedMenuAnchorEl)
    if (isSameParentOpening) {
      closeCollapsedMenu()
      return
    }
    setCollapsedMenuAnchorEl(event.currentTarget)
    setCollapsedMenuParentKey(item.key)
    setCollapsedMenuChildren(nextChildren)
  }

  React.useEffect(() => {
    if (openSidebar || isMobile) closeCollapsedMenu()
  }, [openSidebar, isMobile, closeCollapsedMenu])

  React.useEffect(() => {
    onMobileClose?.()
    closeCollapsedMenu()
  }, [pathname])

  const renderIcon = (icon: any) => {
    if (!icon) return null
    if (React.isValidElement(icon)) return icon
    return React.createElement(icon)
  }

  const renderMenuItems = (items: MenuItem[], level = 0, rootKey?: string): React.ReactNode =>
    items.map((item) => {
      const hasChildren = Boolean(item.children?.length)
      const isChildLevel = level > 0
      const isRootParent = hasChildren && level === 0
      const effectiveRootKey = level === 0 ? item.key : rootKey
      const selected = isItemSelected(item)
      const label = item.label
      const isExpanded = Object.prototype.hasOwnProperty.call(expandedMenus, item.key)
        ? expandedMenus[item.key]
        : autoExpandedMenus[item.key] ?? false
      const canShowChildren = hasChildren && (openSidebar || isMobile)
      const onCurrentPage = isRouteSelected(item)
      const isSectionHeader = Boolean(hasChildren && isItemSelected(item) && !onCurrentPage)

      const chevronColor = onCurrentPage
        ? cv('shell', 'sidebarActiveText')
        : isSectionHeader
          ? cv('primary', 'main')
          : cv('shell', 'sidebarIcon')

      const listTextColor = onCurrentPage
        ? cv('shell', 'sidebarActiveText')
        : isSectionHeader
          ? cv('primary', 'dark')
          : isChildLevel
            ? cv('neutral', 'textMuted')
            : isRootParent
              ? cv('neutral', 'text')
              : cv('shell', 'sidebarItem')

      const listTextWeight = onCurrentPage || isSectionHeader ? 600 : isRootParent ? 600 : 500

      const roleSx = onCurrentPage
        ? leafActiveSx
        : isSectionHeader
          ? sectionHeaderSx
          : isChildLevel
            ? inactiveChildItemSx
            : isRootParent
              ? inactiveParentRootSx
              : inactiveItemSx

      const iconSlotSx = isChildLevel
        ? { minWidth: 28, '& svg': { fontSize: '1.125rem' } }
        : { minWidth: 32, '& svg': { fontSize: '1.1875rem' } }

      const listTextSx = {
        opacity: openSidebar || isMobile ? 1 : 0,
        width: openSidebar || isMobile ? 'auto' : 0,
        minWidth: 0,
        flex: '1 1 auto' as const,
        paddingLeft: isChildLevel ? 0.5 : 0.625,
        overflow: 'hidden',
        color: listTextColor,
        fontWeight: listTextWeight,
        '& .MuiListItemText-primary': {
          fontSize: isChildLevel ? '0.875rem' : isRootParent ? '0.9375rem' : '0.96875rem',
          letterSpacing: isChildLevel ? '0.008em' : isRootParent ? '0.01em' : '0.0125em',
          lineHeight: isChildLevel ? 1.4 : isRootParent ? 1.42 : 1.45,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        transition: (t: Theme) =>
          t.transitions.create(['opacity', 'width'], {
            duration: t.transitions.duration.enteringScreen,
          }),
      }

      const buttonSx = {
        py: isChildLevel ? 0.5625 : 0.6875,
        pl: (isChildLevel ? 0.375 : 0.5) + level * SIDEBAR_CHILD_INDENT_STEP,
        pr: openSidebar || isMobile ? (isChildLevel ? 0.45 : 0.6) : 0.875,
        minHeight: isChildLevel ? 36 : 42,
        mx: isChildLevel ? 0.25 : 0.5,
        my: isChildLevel ? 0.03125 : 0.0625,
        borderRadius: isChildLevel ? '8px' : '10px',
        ...roleSx,
        ...(!isItemSelected(item) && hasChildren && isExpanded ? expandedParentSx : {}),
      }

      if (hasChildren) {
        const parentButton = (
          <ListItemButton
            key={item.key}
            selected={selected}
            onClick={(event) => handleParentClick(event, item, hasChildren, effectiveRootKey)}
            sx={buttonSx}
          >
            <ListItemIcon sx={iconSlotSx}>{renderIcon(item.icon)}</ListItemIcon>
            <ListItemText primary={label} sx={listTextSx} />
            {(openSidebar || isMobile) && (
              <ChevronRightIcon
                sx={{
                  flexShrink: 0,
                  fontSize: isChildLevel ? '1.03125rem' : '1.125rem',
                  opacity: isExpanded ? 0.95 : 0.65,
                  transition: (t) =>
                    t.transitions.create(['transform', 'opacity'], {
                      duration: t.transitions.duration.shortest,
                    }),
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  color: chevronColor,
                }}
              />
            )}
          </ListItemButton>
        )

        return (
          <React.Fragment key={item.key}>
            {openSidebar || isMobile ? (
              parentButton
            ) : (
              <Tooltip title={label} placement="right" arrow>
                <span style={{ display: 'block' }}>{parentButton}</span>
              </Tooltip>
            )}
            {canShowChildren && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List disablePadding sx={{ ...SIDEBAR_NESTED_LIST_SX, boxSizing: 'border-box' }}>
                  {renderMenuItems(item.children ?? [], level + 1, effectiveRootKey)}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        )
      }

      const button = (
        <ListItemButton
          key={item.key}
          component={Link}
          to={item.path || '/'}
          selected={selected}
          onClick={isMobile ? onMobileClose : undefined}
          sx={buttonSx}
        >
          <ListItemIcon sx={iconSlotSx}>{renderIcon(item.icon)}</ListItemIcon>
          <ListItemText primary={label} sx={listTextSx} />
        </ListItemButton>
      )

      return openSidebar || isMobile ? (
        <React.Fragment key={item.key}>{button}</React.Fragment>
      ) : (
        <Tooltip key={item.key} title={label} placement="right" arrow>
          <span style={{ display: 'block' }}>{button}</span>
        </Tooltip>
      )
    })

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      <List
        sx={{
          flex: 1,
          minHeight: 0,
          pt: 0.875,
          pb: 0.625,
          px: 0.125,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {renderMenuItems(visibleMenuItems)}
      </List>
      <Popover
        open={Boolean(collapsedMenuAnchorEl)}
        anchorEl={collapsedMenuAnchorEl}
        onClose={closeCollapsedMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: DRAWER_WIDTH,
            maxWidth: 300,
            maxHeight: '70vh',
            overflowY: 'auto',
            borderRadius: '12px',
            backgroundColor: cv('shell', 'popoverBg'),
            border: `1px solid ${cv('shell', 'popoverBorder')}`,
            boxShadow: cv('shell', 'popoverShadow'),
          },
        }}
      >
        <List sx={{ py: 0.75, px: 0.75 }}>
          {collapsedMenuChildren.map((child) => {
            const childSelected = isItemSelected(child)
            return (
              <ListItemButton
                key={child.key}
                component={Link}
                to={child.path || '/'}
                selected={childSelected}
                onClick={closeCollapsedMenu}
                sx={
                  childSelected
                    ? {
                        py: 0.5,
                        px: 0.75,
                        minHeight: 34,
                        mb: 0.0625,
                        borderRadius: '8px',
                        '& .MuiListItemText-root': { minWidth: 0 },
                        ...leafActiveSx,
                        '& .MuiListItemIcon-root': {
                          minWidth: 28,
                          color: `${cv('shell', 'sidebarActiveIcon')} !important`,
                        },
                        '& .MuiListItemIcon-root svg': { fontSize: '1.0625rem' },
                        '& .MuiListItemText-primary': { fontSize: '0.875rem' },
                      }
                    : {
                        py: 0.5,
                        px: 0.75,
                        minHeight: 34,
                        mb: 0.0625,
                        borderRadius: '8px',
                        border: '1px solid transparent',
                        '& .MuiListItemText-root': { minWidth: 0 },
                        '& .MuiListItemIcon-root': {
                          minWidth: 28,
                          color: `${cv('neutral', 'textSubtle')} !important`,
                        },
                        '& .MuiListItemIcon-root svg': { fontSize: '1.0625rem' },
                        color: cv('neutral', 'textMuted'),
                        fontWeight: 500,
                        '&:hover': { backgroundColor: cv('neutral', 'backgroundDeep') },
                      }
                }
              >
                <ListItemIcon>{renderIcon(child.icon)}</ListItemIcon>
                <ListItemText
                  primary={child.label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: '0.875rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              </ListItemButton>
            )
          })}
        </List>
      </Popover>
    </Box>
  )

  const drawerSx = isMobile
    ? {
        zIndex: (theme: Theme) => theme.zIndex.drawer,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: LAYOUT_APP_BAR_HEIGHT_PX,
          left: 0,
          height: `calc(100vh - ${LAYOUT_APP_BAR_HEIGHT_PX}px)`,
          ...paperSx,
        },
      }
    : {
        width: drawerWidth,
        flex: '0 0 auto',
        flexShrink: 0,
        height: '100%',
        alignSelf: 'stretch',
        position: 'relative',
        zIndex: 0,
        transition: (theme: Theme) =>
          theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        '& .MuiDrawer-paper': {
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          boxSizing: 'border-box',
          zIndex: 0,
          transition: (theme: Theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          ...paperSx,
        },
      }

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      {...(isMobile
        ? {
            open: mobileOpen,
            onClose: onMobileClose,
            ModalProps: { keepMounted: true },
          }
        : { open: true })}
      sx={drawerSx}
    >
      {drawerContent}
    </Drawer>
  )
}

export default SidebarMenu
export { DRAWER_WIDTH }
